import Component from '@glimmer/component';
import ActiveFiltersZone from '../report-builder/active-filters-zone.gts';
import {
  flattenLayout,
  appendFilter,
  appendGroup,
  removeFieldFromLayout,
  moveFieldToGroup,
  moveFieldOutOfGroup,
  dissolveGroup,
  type FilterOperator,
  type FilterLayout,
} from '../../utils/filter-operators.ts';
import type { FieldOption } from '../../utils/opensearch-field-mapper.ts';
import type {
  BuildFilterConfig,
  UserFilterConfig,
} from '../../utils/dashboard-builder/widget-config-types.ts';

// FilterEditor – a thin wrapper around the report builder's ActiveFiltersZone
// that owns add/remove/value/operator/group handlers over a BuildFilterConfig
// (build mode, with OR-group layout) or UserFilterConfig (user mode, flat) and
// reports a new config via onChange. Reused by the widget editor, the
// dashboard filter panel, and the read-only view mode.

type AnyFilterConfig = BuildFilterConfig | UserFilterConfig;

interface FilterEditorSignature {
  Args: {
    /** 'build' adds an OR-group layout; 'user' is a flat list. */
    mode: 'build' | 'user';
    title?: string;
    subtitle?: string;
    /** Filterable fields for the widget/dashboard's dataset. */
    fields: FieldOption[];
    config: AnyFilterConfig;
    onChange: (config: AnyFilterConfig) => void;
    /** Hide the add-filter picker (e.g. view mode adjusts a fixed set). @default true */
    allowAdd?: boolean;
    /** Per-field distinct values (dropdown options) — see distinct-values-resolver. */
    distinctValuesMap?: Record<string, string[]>;
    /**
     * The build filters that constrain this (user-mode) editor's fields, so a
     * viewer's date filter can be clamped/warned against the hard window they
     * impose. Only meaningful in user mode; omit in build mode.
     */
    buildFilterValues?: Record<string, unknown>;
    buildFilterLayout?: FilterLayout;
  };
  Element: HTMLDivElement;
}

export default class FilterEditor extends Component<FilterEditorSignature> {
  get isBuild() {
    return this.args.mode === 'build';
  }

  get allowAdd() {
    return this.args.allowAdd ?? true;
  }

  /** With a fixed set (allowAdd=false, e.g. view mode), viewers can adjust
   * values but not remove the filter itself — omit the remove handler so the
   * ✕ never renders. */
  get removeHandler() {
    return this.allowAdd ? this.removeFilter : undefined;
  }

  get layout(): FilterLayout {
    return (this.args.config as BuildFilterConfig).filterLayout ?? [];
  }

  get selectedKeys(): string[] {
    return this.isBuild ? flattenLayout(this.layout) : (this.args.config.selectedFilters ?? []);
  }

  get fieldByKey(): Record<string, FieldOption> {
    const map: Record<string, FieldOption> = {};
    for (const f of this.args.fields) map[f.key] = f;
    return map;
  }

  get selectedFields(): FieldOption[] {
    return this.selectedKeys.map((k) => this.fieldByKey[k]).filter(Boolean) as FieldOption[];
  }

  get availableFields(): FieldOption[] {
    const selected = new Set(this.selectedKeys);
    return this.args.fields.filter((f) => !selected.has(f.key));
  }

  #emit(patch: Partial<BuildFilterConfig>) {
    this.args.onChange({ ...this.args.config, ...patch } as AnyFilterConfig);
  }

  #cleanValues(values: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(values)) {
      if (v !== undefined && v !== null) out[k] = v;
    }
    return out;
  }

  addFilter = (event: Event) => {
    const select = event.target as HTMLSelectElement;
    const key = select.value;
    select.value = '';
    if (!key || this.selectedKeys.includes(key)) return;
    if (this.isBuild) {
      const layout = appendFilter(this.layout, key);
      this.#emit({ filterLayout: layout, selectedFilters: flattenLayout(layout) });
    } else {
      this.#emit({ selectedFilters: [...this.selectedKeys, key] });
    }
  };

  removeFilter = (field: FieldOption) => {
    const values = { ...this.args.config.filterValues };
    for (const k of Object.keys(values)) {
      if (k === field.key || k.startsWith(`${field.key}__`)) delete values[k];
    }
    if (this.isBuild) {
      const layout = removeFieldFromLayout(this.layout, field.key);
      this.#emit({
        filterLayout: layout,
        selectedFilters: flattenLayout(layout),
        filterValues: values,
      });
    } else {
      this.#emit({
        selectedFilters: this.selectedKeys.filter((k) => k !== field.key),
        filterValues: values,
      });
    }
  };

  updateValue = (key: string, value: unknown) => {
    this.#emit({
      filterValues: this.#cleanValues({ ...this.args.config.filterValues, [key]: value }),
    });
  };

  updateValues = (updates: Record<string, unknown>) => {
    this.#emit({
      filterValues: this.#cleanValues({ ...this.args.config.filterValues, ...updates }),
    });
  };

  changeOperator = (fieldKey: string, operator: FilterOperator) => {
    this.#emit({ filterOperators: { ...this.args.config.filterOperators, [fieldKey]: operator } });
  };

  addGroup = () => this.#emit({ filterLayout: appendGroup(this.layout) });
  dissolveGroupAt = (groupId: string) =>
    this.#emit({ filterLayout: dissolveGroup(this.layout, groupId) });
  moveToGroup = (fieldKey: string, groupId: string) =>
    this.#emit({ filterLayout: moveFieldToGroup(this.layout, fieldKey, groupId) });
  moveOutOfGroup = (fieldKey: string) =>
    this.#emit({ filterLayout: moveFieldOutOfGroup(this.layout, fieldKey) });

  <template>
    <div data-test-filter-editor ...attributes>
      {{#if this.allowAdd}}
        <select
          class="mb-2 w-full rounded-lg border tio-border-default px-2 py-1.5 text-sm"
          aria-label="Add filter"
          {{on "change" this.addFilter}}
        >
          <option value="">+ Add filter…</option>
          {{#each this.availableFields as |f|}}
            <option value={{f.key}}>{{f.label}}</option>
          {{/each}}
        </select>
      {{/if}}

      {{#if this.isBuild}}
        <ActiveFiltersZone
          @title={{@title}}
          @subtitle={{@subtitle}}
          @filters={{this.selectedFields}}
          @filterValues={{@config.filterValues}}
          @filterOperators={{@config.filterOperators}}
          @distinctValuesMap={{@distinctValuesMap}}
          @onRemove={{this.removeHandler}}
          @onUpdateValue={{this.updateValue}}
          @onUpdateValues={{this.updateValues}}
          @onOperatorChange={{this.changeOperator}}
          @layout={{this.layout}}
          @onAddGroup={{this.addGroup}}
          @onDissolveGroup={{this.dissolveGroupAt}}
          @onMoveToGroup={{this.moveToGroup}}
          @onMoveOutOfGroup={{this.moveOutOfGroup}}
        />
      {{else}}
        <ActiveFiltersZone
          @title={{@title}}
          @subtitle={{@subtitle}}
          @filters={{this.selectedFields}}
          @filterValues={{@config.filterValues}}
          @filterOperators={{@config.filterOperators}}
          @distinctValuesMap={{@distinctValuesMap}}
          @onRemove={{this.removeHandler}}
          @onUpdateValue={{this.updateValue}}
          @onUpdateValues={{this.updateValues}}
          @onOperatorChange={{this.changeOperator}}
          @buildFilterValues={{@buildFilterValues}}
          @buildFilterLayout={{@buildFilterLayout}}
        />
      {{/if}}
    </div>
  </template>
}
